import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Sparkles, TrendingUp, AlertCircle, Users, Lightbulb } from "lucide-react";

export default function Insights() {
  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await http.get("/patients")).data,
  });

  const insights = useMemo(() => {
    if (!patients.length) return [];
    const list = [];
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonth = patients.filter((p) => new Date(p.enquiry_date) >= monthStart);

    list.push({
      icon: Users,
      text: `You received ${thisMonth.length} new enquir${thisMonth.length === 1 ? "y" : "ies"} this month.`,
    });

    const bySource = new Map();
    for (const p of patients) bySource.set(p.acquisition_source, (bySource.get(p.acquisition_source) || 0) + 1);
    const top = [...bySource.entries()].sort((a, b) => b[1] - a[1])[0];
    if (top) {
      const [name, count] = top;
      const pct = Math.round((count / patients.length) * 100);
      list.push({ icon: Sparkles, text: `${name} accounted for ${pct}% of your total patient enquiries (${count} of ${patients.length}).` });
    }

    // Per-source conv detail
    for (const [name, total] of bySource.entries()) {
      const converted = patients.filter((p) => p.acquisition_source === name && p.enquiry_status === "Converted to Treatment").length;
      if (total >= 3) {
        list.push({
          icon: TrendingUp,
          text: `${name} generated ${total} enquiries, of which ${converted} converted to treatment.`,
        });
      }
    }

    const unresolved = patients.filter((p) => !["Converted to Treatment", "Lost / Did Not Convert"].includes(p.enquiry_status)).length;
    if (unresolved > 0) {
      list.push({
        icon: AlertCircle,
        text: `You have ${unresolved} enquir${unresolved === 1 ? "y" : "ies"} that have not yet been marked as converted or lost.`,
      });
    }

    const revenue = patients.reduce((a, p) => a + (Number(p.revenue) || 0), 0);
    if (revenue > 0) {
      list.push({ icon: TrendingUp, text: `Total revenue tracked from new patients: ₹ ${revenue.toLocaleString()}.` });
    }

    return list;
  }, [patients]);

  return (
    <div className="space-y-5" data-testid="insights-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900">Insights</h1>
        <p className="text-sm text-zinc-500 mt-1">Simple observations from your own data — no guesswork.</p>
      </div>

      {insights.length === 0 ? (
        <Card className="p-10 border-zinc-200 text-center">
          <Lightbulb className="h-8 w-8 text-zinc-300 mx-auto" />
          <p className="mt-3 text-sm text-zinc-500">Add a few patient records to unlock insights.</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {insights.map((i, idx) => (
            <Card key={idx} className="p-4 border-zinc-200 flex gap-3 items-start" data-testid={`insight-${idx}`}>
              <div className="h-8 w-8 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                <i.icon className="h-4 w-4" strokeWidth={1.75} />
              </div>
              <p className="text-sm text-zinc-800 leading-relaxed pt-1.5">{i.text}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
